// 독립 연결 창(오라클 성능 분석 · DBMS 카탈로그 · 자원 분석 · PL/SQL 편집기 · 세션 분석)이
// **자기 세션을 갖는** 방법. 다섯 창이 이 훅 하나를 쓴다.
//
// 왜 만들었나
// -----------
// 예전에는 다섯 창이 `connectionId` 만 보냈고, 백엔드가 요청마다 일회용 세션을 만들고 닫았다.
// 그래서 조회마다 다른 세션이었고, 화면은 "이 창이 무엇으로 보고 있는지" 를 말할 수가 없었다.
// PL/SQL 편집기에서는 그 탓에 컴파일과 실행이 다른 세션이라 DBMS_OUTPUT·패키지 상태·
// `ALTER SESSION` 이 이어지지 않았다.
//
// 지켜야 할 것
// -----------
// 1. **줄을 세운다.** 오라클 세션은 한 번에 한 문장만 돈다. 자원 분석은 열 때 조회 5개를
//    동시에 쏘는데(`fetchStorage(); fetchMemory(); …`), 그대로 한 세션에 보내면 서버가
//    409 busy 로 거절한다. `run()` 이 창 단위로 줄을 세운다 — 우리가 더하는 제약이 아니라
//    세션의 성질이다.
// 2. **창을 닫아도 세션은 닫지 않는다 — 다시 열면 그것을 쓴다**(2026-10-01 결정, 예전에는 닫았다).
//    전담 세션은 (창 키 = label, 연결) 마다 하나다. 열 때 그것이 있으면 다시 쓰고(`findOwnConnSession`),
//    없을 때만 만든다. 그래서 여닫기·새로고침을 해도 세션이 쌓이지 않는다 — 예전에 닫으려고 둔
//    `pagehide` 정리가 필요 없어졌다. 목록에는 「세션 분석 전용」처럼 보이고 탐색기에서 닫을 수 있다.
// 3. 사람이 연 세션은 연결당 poolMax-1 까지다(창 다섯 + 편집기). 못 만들면 서버가
//    `SESSION_LIMIT` 을 주고 화면이 Pool Max 를 늘리라고 안내한다.
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSession } from '../redux/actions/sessionActions';
import createSerialQueue from '../utils/serialQueue';
import { findOwnConnSession } from '../utils/sessionDisplay';
import { formatApiError } from '../utils/formatters';
import i18n from '../i18n';

// 'user' 가 아니라 'monitor' 다 — SessionPicker 가 에디터 탭 바인딩 후보에서 빼야 한다
// (독립 연결 창의 세션이지 편집기의 것이 아니다). pool 상한에는 'user' 와 똑같이 걸린다.
export const MONITOR_CATEGORY = 'monitor';

// 연결 후보 필터. **모듈 스코프에 두어야** 참조가 고정된다 — 렌더마다 새 함수를 넘기면
// 아래 `conns` useMemo 가 매번 새 배열을 내고, 그것을 보는 effect 들이 끝없이 돈다.
/** GV$·AWR 을 읽는 창 — 오라클 전용 (dbType 미지정은 옛 저장물이라 오라클로 본다) */
export const ORACLE_CONN = (c) => c.dbType === 'oracle' || !c.dbType;
/** 카탈로그처럼 PostgreSQL 도 보는 창 */
export const ORACLE_OR_PG_CONN = (c) => c.dbType === 'oracle' || c.dbType === 'postgresql' || !c.dbType;

/**
 * @param {object}   opts
 * @param {boolean}  opts.isOpen              창이 열려 있는가
 * @param {Array}    opts.connections         연결 목록
 * @param {string}   opts.defaultConnectionId 열 때 쓸 연결 (없으면 목록 첫 번째)
 * @param {function} opts.filter              연결 후보 필터 (dbType 이 창마다 다르다)
 * @param {string}   opts.label               창 키 — 전담 세션의 라벨이고 재사용의 기준이다
 *                                            (utils/sessionDisplay 의 OWN_CONN_WINDOW_KEYS)
 */
export default function useOwnConnSession({ isOpen, connections = [], defaultConnectionId, filter, label }) {
    const dispatch = useDispatch();
    const sessionsList = useSelector(s => s.sessions?.list || []);

    const conns = useMemo(
        () => (filter ? connections.filter(filter) : connections),
        [connections, filter],
    );

    const [connId, setConnId] = useState(null);
    const [sessionId, setSessionId] = useState(null);
    const [sessionError, setSessionError] = useState(null);
    const [creating, setCreating] = useState(false);

    // 사용자가 **직접 고른** 세션인가(다른 창·편집기의 것).
    // 고르면 연결도 함께 바뀔 수 있는데(다른 연결의 세션), 그러면 아래 effect 가 연결 변경으로
    // 보고 **이 창의 세션으로 되돌려 방금 고른 것을 덮어쓴다.** 그것을 막는다.
    const manualRef = useRef(false);
    // 창 단위 직렬 큐. 한 세션은 한 번에 한 문장이라 여기서 줄을 세운다.
    // 큐 자체는 순수 함수라 `utils/__tests__/serialQueue.test.js` 가 검사한다.
    const queueRef = useRef(null);
    if (queueRef.current === null) queueRef.current = createSerialQueue();
    // 재사용 판정은 **그 순간의** 목록으로 한다 — 목록을 의존성에 넣으면 폴링마다 다시 고른다
    const listRef = useRef(sessionsList);
    listRef.current = sessionsList;

    // ── 연결 고르기 ────────────────────────────────────────────
    // **열 때마다** 받은 연결을 쓴다. 창을 여는 쪽(Header)이 「이 창의 세션이 있던 연결」이나
    // 사용자가 연결 선택 창에서 고른 것을 넘긴다 — 앞에 쓰던 값을 고집하면 그 선택이 무시된다.
    const wasOpenRef = useRef(false);
    useEffect(() => {
        const opening = isOpen && !wasOpenRef.current;
        wasOpenRef.current = isOpen;
        if (!isOpen) return;
        if (opening) manualRef.current = false;
        setConnId(prev => {
            const valid = (id) => !!id && conns.some(c => c.id === id);
            if (opening && valid(defaultConnectionId)) return defaultConnectionId;
            if (valid(prev)) return prev;
            if (valid(defaultConnectionId)) return defaultConnectionId;
            return conns.length > 0 ? conns[0].id : null;
        });
    }, [isOpen, conns, defaultConnectionId]);

    // ── 세션 다시 쓰기 / 만들기 ─────────────────────────────────
    useEffect(() => {
        if (!isOpen || !connId) return undefined;
        if (manualRef.current) return undefined;   // 사용자가 고른 세션을 쓰는 중이다
        const reuse = findOwnConnSession(listRef.current, label, connId);
        if (reuse) {
            setSessionId(reuse.sessionId);
            setSessionError(null);
            setCreating(false);
            return undefined;
        }
        let cancelled = false;
        setSessionId(null);
        setCreating(true);
        setSessionError(null);
        dispatch(createSession(connId, MONITOR_CATEGORY, label))
            .then(s => {
                // 만드는 사이에 창이 닫혀도 닫지 않는다 — 이 창의 세션이라 다음에 열 때 쓴다
                if (cancelled) return;
                setSessionId(s?.sessionId || s?.id || null);
            })
            .catch(err => {
                if (cancelled) return;
                // 세션을 못 만들어도 창은 뜬다 — sessionId 없이 보내면 백엔드가
                // 예전처럼 일회용 세션으로 답한다. 다만 그 사실을 화면이 말한다.
                // 한도에 걸린 것이면(SESSION_LIMIT) Pool Max 를 늘리라는 안내가 번역되어 나온다.
                setSessionId(null);
                setSessionError(formatApiError(err, i18n.t.bind(i18n)));
            })
            .finally(() => { if (!cancelled) setCreating(false); });

        return () => { cancelled = true; };
    }, [isOpen, connId, label, dispatch]);

    // 쓰던 세션이 사라졌다(탐색기에서 닫았거나 서버가 정리했다) — 그 세션 id 로 계속 보내면 404 다.
    // 비우면 조회는 일회용으로 돌고, 다음에 열 때 새로 만든다.
    // 한 번도 목록에 안 보였던 것은 건드리지 않는다(만든 직후, 폴링 전이다).
    const seenRef = useRef(new Set());
    useEffect(() => {
        sessionsList.forEach(s => { if (s && s.sessionId) seenRef.current.add(s.sessionId); });
        if (!sessionId || !seenRef.current.has(sessionId)) return;
        if (sessionsList.some(s => s.sessionId === sessionId)) return;
        setSessionId(null);
        manualRef.current = false;
    }, [sessionsList, sessionId]);

    // ── 다른 세션 고르기 ───────────────────────────────────────
    // 이 창의 세션은 닫지 않는다 — 연결을 다시 고르면 그것으로 돌아온다.
    const pickSession = useCallback((nextId, nextConnId) => {
        manualRef.current = true;
        setSessionId(nextId);
        setSessionError(null);
        if (nextConnId) setConnId(nextConnId);
    }, []);

    /**
     * 연결 고르기(제목줄의 드롭다운). 그 연결의 **이 창 세션**을 쓴다 — 있으면 다시 쓰고 없으면 만든다.
     * 고른 세션을 쓰던 중이었다면 거기서 손을 뗀다. 이것이 `pickSession` 과 갈리는 지점이다.
     */
    const changeConn = useCallback((nextConnId) => {
        const wasManual = manualRef.current;
        manualRef.current = false;
        setConnId(prev => {
            // 고른 세션을 쓰다 같은 연결을 다시 고르면 값이 같아 effect 가 안 돈다 — 직접 되돌린다
            if (prev === nextConnId && wasManual) {
                const reuse = findOwnConnSession(listRef.current, label, nextConnId);
                setSessionId(reuse ? reuse.sessionId : null);
            }
            return nextConnId;
        });
    }, [label]);

    // ── 직렬 큐 ────────────────────────────────────────────────
    /**
     * 창의 모든 조회를 이것으로 감싼다. 앞 조회가 끝나야 다음이 나간다.
     * 앞 조회가 실패해도 줄은 이어진다 — 한 번 실패로 창이 굳으면 안 된다.
     */
    const run = useCallback((thunk) => queueRef.current(thunk), []);

    const session = useMemo(
        () => sessionsList.find(s => s.sessionId === sessionId) || null,
        [sessionsList, sessionId],
    );

    return {
        conns,
        connId,
        setConnId: changeConn,
        sessionId,
        session,
        sessionError,
        creating,
        pickSession,
        run,
        /** 조회에 실어 보낼 값 — sessionId 가 없으면 아예 싣지 않는다(백엔드가 일회용으로 답한다) */
        params: useMemo(
            () => (sessionId ? { connectionId: connId || undefined, sessionId }
                : { connectionId: connId || undefined }),
            [connId, sessionId],
        ),
    };
}
